// src/utils/naturalLanguageSearch.js

/**
 * Parse a natural language query into structured filter parameters
 * @param {string} query - The natural language query from the user
 * @return {Object} A structured object with extracted search parameters
 */
export function parseNaturalLanguageQuery(query) {
  if (!query || typeof query !== 'string') {
    return { searchTerm: '' };
  }

  const result = {
    searchTerm: '',
    minPlayers: null,
    maxPlayers: null,
    maxTime: null,
    weight: null,
    themes: [],
    mechanics: []
  };

  // Store original query for debugging
  const originalQuery = query;

  // Convert numbers written as text to digits
  query = convertTextNumbersToDigits(query);

  // Extract exact player count with improved patterns
  const exactPlayerPatterns = [
    /(\d+)\s*(?:player|players|people|person)\b/i,
    /(?:for|with)\s*(\d+)\s*(?:player|players|people|person)\b/i,
    /(?:plays|works|good|great|best)\s*with\s*(\d+)\s*(?:player|players|people|person)\b/i,
    /(?:plays|works|good|great|best)\s*for\s*(\d+)\s*(?:player|players|people|person)\b/i,
    /game for (\d+)/i,
    /(\d+) person game/i,
    /(\d+)p\b/i, // Match shorthand like "4p"
  ];

  for (const pattern of exactPlayerPatterns) {
    const match = query.match(pattern);
    if (match && match[1]) {
      const playerCount = parseInt(match[1], 10);
      result.players = playerCount;
      // Remove the matched player text from the query
      query = query.replace(match[0], ' ');
      break; // Stop after finding the first match
    }
  }

  // Extract player range (e.g., "2-4 players", "2 to 4 players")
  const playerRangePatterns = [
    /(\d+)\s*(?:-|to)\s*(\d+)\s*(?:player|players|people|person)/i,
    /(?:for|with)\s*(\d+)\s*(?:-|to)\s*(\d+)\s*(?:player|players|people|person)/i,
    /(?:plays|works|good|great|best)\s*with\s*(\d+)\s*(?:-|to)\s*(\d+)\s*(?:player|players|people|person)/i,
    /(?:plays|works|good|great|best)\s*for\s*(\d+)\s*(?:-|to)\s*(\d+)\s*(?:player|players|people|person)/i,
    /(\d+)-(\d+)p\b/i, // Match shorthand like "2-4p"
  ];

  for (const pattern of playerRangePatterns) {
    const match = query.match(pattern);
    if (match && match[1] && match[2]) {
      result.players = parseInt(match[1], 10);
      // Remove the matched player range from the query
      query = query.replace(match[0], ' ');
      break;
    }
  }

  // Extract "at least X players" or "X+ players"
  const minPlayerPatterns = [
    /(?:at least|minimum)\s*(\d+)\s*(?:player|players|people|person)/i,
    /(\d+)\+\s*(?:player|players|people|person)/i,
    /(\d+) or more (?:player|players|people|person)/i,
    /works with (\d+)\+/i,
  ];

  for (const pattern of minPlayerPatterns) {
    const match = query.match(pattern);
    if (match && match[1]) {
      result.players = parseInt(match[1], 10);
      // Remove the matched player text from the query
      query = query.replace(match[0], ' ');
      break;
    }
  }

  // Extract "up to X players" or "maximum X players"
  const maxPlayerPatterns = [
    /(?:up to|maximum|max)\s*(\d+)\s*(?:player|players|people|person)/i,
    /(?:plays|works|good|great|best)\s*with\s*(?:up to|maximum|max)\s*(\d+)\s*(?:player|players|people|person)/i,
    /(\d+) (?:player|players|people|person) or less/i,
    /no more than (\d+) (?:player|players|people|person)/i,
  ];

  for (const pattern of maxPlayerPatterns) {
    const match = query.match(pattern);
    if (match && match[1]) {
      result.players = parseInt(match[1], 10);
      // Remove the matched player text from the query
      query = query.replace(match[0], ' ');
      break;
    }
  }

  // Parse solo game requests
  const soloPatterns = [
    /\b(?:solo|single player|1 player|one player|by myself|alone)\b/i,
    /\bsolitaire\b/i,
    /\bplayable solo\b/i,
    /\bcan play (alone|solo|by myself)\b/i,
    /\b1p\b/i, // Match "1p" shorthand
  ];

  for (const pattern of soloPatterns) {
    if (pattern.test(query)) {
      result.players = 1;
      // Remove the matched solo text from the query
      query = query.replace(pattern, ' ');
      break;
    }
  }

  // Extract play time with expanded patterns
  const timePatterns = [
    /(\d+)\s*(?:min|minute|minutes)/i,
    /under\s*(\d+)\s*(?:min|minute|minutes)/i,
    /less\s*than\s*(\d+)\s*(?:min|minute|minutes)/i,
    /(?:max|maximum)\s*(?:of)?\s*(\d+)\s*(?:min|minute|minutes)/i,
    /(?:quick|fast|short)\s*(?:game|games)?/i,
    /(?:play|plays|played) in (?:under|less than)?\s*(\d+)\s*(?:min|minute|minutes)/i,
    /(?:play|plays|played) in (?:about)?\s*(\d+)\s*(?:min|minute|minutes)/i,
    /(\d+)\s*(?:min|minute|minutes) or less/i,
    /(\d+)\s*(?:min|minute|minutes) max/i,
    /take(?:s)? (?:about|around)?\s*(\d+)\s*(?:min|minute|minutes)/i,
    /(\d+)\s*(?:min|minute|minutes) playtime/i,
    /playtime (?:of|under|about)?\s*(\d+)\s*(?:min|minute|minutes)/i,
    /finished in (?:under|about)?\s*(\d+)\s*(?:min|minute|minutes)/i,
    /fast (?:paced|playing)/i,
    /filler game/i,
  ];

  for (const pattern of timePatterns) {
    const match = query.match(pattern);
    if (match) {
      // Special cases for terms without explicit minutes
      if (/(?:quick|fast|short|filler)/i.test(match[0])) {
        result.maxTime = '30'; // Set to 30 minutes for quick/fast/short/filler games
      } else if (match[1]) {
        // For "played in about X minutes", just use X as maxTime
        result.maxTime = match[1];
      }
      // Remove the matched time text from the query
      query = query.replace(match[0], ' ');
      break;
    }
  }

  // Extract minimum age with expanded patterns
  const agePatterns = [
    /(\d+)\s*(?:\+|plus)(?:\s*years)?(?:\s*old)?/i,
    /age\s*(\d+)(?:\s*\+|\s*and\s*up|\s*and\s*over|\s*or\s*older)?/i,
    /for\s*(?:ages?\s*)?(\d+)(?:\s*\+|\s*and\s*up|\s*and\s*over|\s*or\s*older)?/i,
    /(?:suitable|appropriate|good) for (?:age|ages)?\s*(\d+)(?:\s*\+|\s*and\s*up)?/i,
    /(?:kids|children) (?:age|aged)?\s*(\d+)(?:\s*\+|\s*and\s*up)?/i,
    /(?:year|years) olds?\s*(\d+)(?:\s*\+|\s*and\s*up)?/i,
    /(\d+) year olds?/i,
    /playable by (\d+) year olds?/i,
    /for\s*(\d+)\s*year\s*olds?/i,
  ];

  for (const pattern of agePatterns) {
    const match = query.match(pattern);
    if (match && match[1]) {
      
      // If the age is for children (under 12), add the Kids theme
      if (parseInt(match[1]) <= 12 && !result.themes.includes('Kids')) {
        result.themes.push('Kids');
      }
      
      // Remove the matched age text from the query
      query = query.replace(match[0], ' ');
      break;
    }
  }

  // Special pattern for "kids", "family", "children" without specific age
  if (/\b(?:kids?|family|child(?:ren)?)\b/i.test(query)) {
    query = query.replace(/\b(?:kids?|family|child(?:ren)?)\b/i, ' ');
    
    // Add Kids theme if keywords match
    if (/\b(?:kids?|kid's|child(?:ren)?|children's)\b/i.test(originalQuery)) {
      if (!result.themes.includes('Kids')) {
        result.themes.push('Kids');
      }
    }
  }

  // Extract game weight/complexity with expanded mappings
  const weightPatterns = [
    /\b(light|medium light|medium|medium heavy|heavy)\b(?:\s*weight|\s*complexity)?/i,
    /\b(easy|simple|beginner|casual|complex|complicated|difficult|hard|intermediate|advanced)\b(?:\s*to\s*(?:play|learn))?/i,
    /\b(lightweight|middleweight|heavyweight)\b/i,
    /\b(family weight|gateway|introductory|entry level|brain burner|thinky|crunchy)\b/i,
    /\b(high complexity|low complexity|medium complexity)\b/i,
    /\b(rules? heavy|rules? light)\b/i,
  ];

  const weightMap = {
    // Light category
    'light': 'Light',
    'easy': 'Light',
    'simple': 'Light',
    'beginner': 'Light',
    'casual': 'Light',
    'lightweight': 'Light',
    'family weight': 'Light',
    'gateway': 'Light',
    'introductory': 'Light',
    'entry level': 'Light',
    'low complexity': 'Light',
    'rules light': 'Light',
    
    // Medium Light category
    'medium light': 'Medium Light',
    
    // Medium category
    'medium': 'Medium',
    'intermediate': 'Medium',
    'middleweight': 'Medium',
    'medium complexity': 'Medium',
    
    // Medium Heavy category
    'medium heavy': 'Medium Heavy',
    
    // Heavy category
    'heavy': 'Heavy',
    'complex': 'Heavy',
    'complicated': 'Heavy',
    'difficult': 'Heavy',
    'hard': 'Heavy',
    'advanced': 'Heavy',
    'heavyweight': 'Heavy',
    'brain burner': 'Heavy',
    'thinky': 'Heavy',
    'crunchy': 'Heavy',
    'high complexity': 'Heavy',
    'rules heavy': 'Heavy'
  };

  for (const pattern of weightPatterns) {
    const match = query.match(pattern);
    if (match && match[1]) {
      const weightKey = match[1].toLowerCase();
      if (weightMap[weightKey]) {
        result.weight = weightMap[weightKey];
        // Remove the matched weight text from the query
        query = query.replace(match[0], ' ');
        break;
      }
    }
  }

  // Special case for "Quick & Easy" preset
  if (/\b(quick and easy|easy and quick|quick & easy|fast and easy|easy and fast)\b/i.test(query)) {
    result.weight = 'Light';
    result.maxTime = '60';
    query = query.replace(/\b(quick and easy|easy and quick|quick & easy|fast and easy|easy and fast)\b/i, ' ');
  }

  // Extract common themes with expanded themes list
  const themeKeywords = {
    // Common fictional settings
    'zombie': 'Zombies',
    'zombies': 'Zombies',
    'fantasy': 'Fantasy',
    'medieval': 'Medieval',
    'sci-fi': 'Science Fiction',
    'science fiction': 'Science Fiction',
    'horror': 'Horror',
    'space': 'Space',
    'outer space': 'Space',
    'pirates': 'Pirates',
    'pirate': 'Pirates',
    'western': 'Western',
    'war': 'War',
    'city building': 'City Building',
    'city build': 'City Building',
    'civilization': 'Civilization',
    'exploration': 'Exploration',
    'explore': 'Exploration',
    'dungeon': 'Dungeon Crawl',
    'dungeon crawl': 'Dungeon Crawl',

    // Historical periods
    'historical': 'Historical',
    'ancient': 'Ancient',
    'ancient rome': 'Ancient Rome',
    'middle ages': 'Medieval',
    'renaissance': 'Renaissance',
    'victorian': 'Victorian',
    'world war': 'World War',
    'world war 2': 'World War II',
    'ww2': 'World War II',
    'wwii': 'World War II',
    'modern': 'Modern',
    'futuristic': 'Science Fiction',

    // Genre themes
    'adventure': 'Adventure',
    'mystery': 'Mystery',
    'detective': 'Detective',
    'crime': 'Crime',
    'comedy': 'Humor',
    'humor': 'Humor',
    'funny': 'Humor',
    'romance': 'Romance',
    'survival': 'Survival',
    'post-apocalyptic': 'Post-Apocalyptic',
    'apocalypse': 'Post-Apocalyptic',
    'cyberpunk': 'Cyberpunk',

    // Thematic categories
    'animals': 'Animals',
    'animal': 'Animals',
    'nature': 'Nature',
    'natural': 'Nature',
    'economics': 'Economics',
    'economic': 'Economics',
    'trading': 'Trading',
    'trade': 'Trading',
    'farming': 'Farming',
    'farm': 'Farming',
    'mythology': 'Mythology',
    'mythological': 'Mythology',
    'greek mythology': 'Greek Mythology',
    'magic': 'Magic',
    'magical': 'Magic',
    'trains': 'Trains',
    'train': 'Trains',
    'transportation': 'Transportation',
    'transport': 'Transportation',
    'racing': 'Racing',
    'race': 'Racing',
    'sports': 'Sports',
    'sport': 'Sports',
    'cooking': 'Cooking',
    'cook': 'Cooking',
    'food': 'Food',
    'art': 'Art',
    'artistic': 'Art',
    'music': 'Music',
    'musical': 'Music',
    'books': 'Literature',
    'book': 'Literature',
    'literature': 'Literature',
    'literary': 'Literature',
    'movies': 'Movies',
    'movie': 'Movies',
    'tv': 'Television',
    'television': 'Television',

    // Popular specific themes
    'star wars': 'Star Wars',
    'trek': 'Star Trek',
    'star trek': 'Star Trek',
    'lord of the rings': 'Lord of the Rings',
    'lotr': 'Lord of the Rings',
    'tolkien': 'Lord of the Rings',
    'cthulhu': 'Cthulhu',
    'lovecraft': 'Cthulhu',
    'lovecraftian': 'Cthulhu',
    'marvel': 'Superheroes',
    'dc': 'Superheroes',
    'superhero': 'Superheroes',
    'superheroes': 'Superheroes',
    'harry potter': 'Magic',
    'wizard': 'Magic',
    'wizards': 'Magic',
    'witch': 'Magic',
    'witches': 'Magic',
    'dragon': 'Dragons',
    'dragons': 'Dragons',
    'vampire': 'Horror',
    'vampires': 'Horror',
    'undead': 'Zombies',
    'robot': 'Science Fiction',
    'robots': 'Science Fiction',
    'alien': 'Science Fiction',
    'aliens': 'Science Fiction',
    
    // Add cards as theme - fix this in the DB
    'card': 'Card Game',
    'cards': 'Card Game',

    //kids
    'kids': 'Kids',
    'kid': 'Kids',
    'children': 'Kids',
    'child': 'Kids',
    'children\'s': 'Kids',
    'kid\'s': 'Kids',

  };

  // Expanded mechanics list
  const mechanicKeywords = {
    // Card-focused mechanics
    'deck building': 'Deck Building',
    'deck-building': 'Deck Building',
    'deckbuilding': 'Deck Building',
    'deck builder': 'Deck Building',
    'deck-builder': 'Deck Building',
    'build deck': 'Deck Building',
    'building deck': 'Deck Building',
    'card drafting': 'Card Drafting',
    'drafting': 'Card Drafting',
    'draft': 'Card Drafting',
    'draft cards': 'Card Drafting',
    'drafting cards': 'Card Drafting',
    'hand management': 'Hand Management',
    'manage hand': 'Hand Management',
    'managing hand': 'Hand Management',
    'manage cards': 'Hand Management',
    'trick taking': 'Trick-Taking',
    'trick-taking': 'Trick-Taking',
    'take tricks': 'Trick-Taking',
    'taking tricks': 'Trick-Taking',
    'card game': 'Card Game',
    'card games': 'Card Game',
    'cards': 'Card Game',
    'card': 'Card Game',
    'card based': 'Card Game',
    'card-based': 'Card Game',
    'collectible card': 'Collectible Card Game',
    'collectible cards': 'Collectible Card Game',
    'ccg': 'Collectible Card Game',
    'tcg': 'Collectible Card Game',
    'trading card': 'Collectible Card Game',
    'trading cards': 'Collectible Card Game',
    'lcg': 'Living Card Game',
    'living card': 'Living Card Game',
    'living card game': 'Living Card Game',

    // Board control mechanics
    'worker placement': 'Worker Placement',
    'worker-placement': 'Worker Placement',
    'place worker': 'Worker Placement',
    'place workers': 'Worker Placement',
    'area control': 'Area Control',
    'area-control': 'Area Control',
    'area majority': 'Area Control',
    'area-majority': 'Area Control',
    'majority': 'Area Control',
    'area influence': 'Area Control',
    'influence': 'Area Control',
    'area movement': 'Area Movement',
    'area-movement': 'Area Movement',
    'tile placement': 'Tile Placement',
    'tile-placement': 'Tile Placement',
    'place tile': 'Tile Placement',
    'place tiles': 'Tile Placement',
    'modular board': 'Modular Board',
    'modular-board': 'Modular Board',
    'grid movement': 'Grid Movement',
    'grid-movement': 'Grid Movement',
    'hex grid': 'Hex-and-Counter',
    'hex-grid': 'Hex-and-Counter',
    'hexes': 'Hex-and-Counter',
    'map': 'Map Building',
    'mapping': 'Map Building',
    'route building': 'Route/Network Building',
    'route-building': 'Route/Network Building',
    'network building': 'Route/Network Building',
    'network-building': 'Route/Network Building',
    'point to point': 'Point to Point Movement',
    'point-to-point': 'Point to Point Movement',

    // Dice mechanics
    'dice rolling': 'Dice Rolling',
    'dice-rolling': 'Dice Rolling',
    'roll dice': 'Dice Rolling',
    'rolling dice': 'Dice Rolling',
    'dice': 'Dice Rolling',
    'die': 'Dice Rolling',
    'dice placement': 'Dice Placement',
    'dice-placement': 'Dice Placement',
    'place dice': 'Dice Placement',
    'placing dice': 'Dice Placement',
    'dice allocation': 'Dice Allocation',
    'dice-allocation': 'Dice Allocation',
    'allocate dice': 'Dice Allocation',
    'allocating dice': 'Dice Allocation',
    'dice drafting': 'Dice Drafting',
    'dice-drafting': 'Dice Drafting',
    'draft dice': 'Dice Drafting',
    'drafting dice': 'Dice Drafting',
    'roll and write': 'Roll & Write',
    'roll & write': 'Roll & Write',
    'roll-and-write': 'Roll & Write',
    'roll and mark': 'Roll & Write',
    'roll-and-mark': 'Roll & Write',
    'yahtzee': 'Roll & Write',

    // Game structure mechanics
    'cooperative': 'Cooperative',
    'co-op': 'Cooperative',
    'coop': 'Cooperative',
    'cooperative game': 'Cooperative',
    'co-op game': 'Cooperative',
    'coop game': 'Cooperative',
    'work together': 'Cooperative',
    'working together': 'Cooperative',
    'collaboration': 'Cooperative',
    'collaborative': 'Cooperative',
    'traitor': 'Hidden Traitor',
    'hidden traitor': 'Hidden Traitor',
    'hidden-traitor': 'Hidden Traitor',
    'semi-cooperative': 'Semi-Cooperative',
    'semi-coop': 'Semi-Cooperative',
    'semicoop': 'Semi-Cooperative',
    'semi cooperative': 'Semi-Cooperative',
    'team-based': 'Team-Based',
    'team based': 'Team-Based',
    'teams': 'Team-Based',
    'one vs many': 'One vs Many',
    'one vs all': 'One vs Many',
    'one versus many': 'One vs Many',
    'one against many': 'One vs Many',
    'all against one': 'One vs Many',
    'real-time': 'Real-Time',
    'real time': 'Real-Time',
    'realtime': 'Real-Time',
    'simultaneous action': 'Simultaneous Action Selection',
    'simultaneous actions': 'Simultaneous Action Selection',
    'simultaneous action selection': 'Simultaneous Action Selection',
    'simultaneous-action': 'Simultaneous Action Selection',
    'campaign': 'Campaign / Battle Card Driven',
    'campaign game': 'Campaign / Battle Card Driven',
    'legacy': 'Legacy',
    'legacy game': 'Legacy',
    'storytelling': 'Storytelling',
    'story': 'Storytelling',
    'narrative': 'Storytelling',

    // Resource management
    'engine building': 'Engine Building',
    'engine-building': 'Engine Building',
    'build engine': 'Engine Building',
    'building engine': 'Engine Building',
    'engine builder': 'Engine Building',
    'engine-builder': 'Engine Building',
    'tableau building': 'Tableau Building',
    'tableau-building': 'Tableau Building',
    'build tableau': 'Tableau Building',
    'building tableau': 'Tableau Building',
    'resource management': 'Resource Management',
    'resource-management': 'Resource Management',
    'manage resources': 'Resource Management',
    'managing resources': 'Resource Management',
    'resources': 'Resource Management',
    'set collection': 'Set Collection',
    'set-collection': 'Set Collection',
    'collect sets': 'Set Collection',
    'collecting sets': 'Set Collection',
    'collect set': 'Set Collection',
    'collecting set': 'Set Collection',
    'action points': 'Action Points',
    'action-points': 'Action Points',
    'actions': 'Action Points',
    'action selection': 'Action Selection',
    'action-selection': 'Action Selection',
    'select actions': 'Action Selection',
    'selecting actions': 'Action Selection',
    'choose actions': 'Action Selection',
    'choosing actions': 'Action Selection',
    'worker': 'Worker Placement',
    'auction': 'Auction/Bidding',
    'auctions': 'Auction/Bidding',
    'auctioning': 'Auction/Bidding',
    'bidding': 'Auction/Bidding',
    'bid': 'Auction/Bidding',
    'trading': 'Trading',
    'trade': 'Trading',
    'trades': 'Trading',
    'negotiation': 'Negotiation',
    'negotiate': 'Negotiation',
    'negotiating': 'Negotiation',
    'bargaining': 'Negotiation',
    'bargain': 'Negotiation',

    // Player interaction
    'bluffing': 'Bluffing',
    'bluff': 'Bluffing',
    'hidden roles': 'Hidden Roles',
    'hidden-roles': 'Hidden Roles',
    'hidden role': 'Hidden Roles',
    'hidden-role': 'Hidden Roles',
    'secret roles': 'Hidden Roles',
    'secret-roles': 'Hidden Roles',
    'secret role': 'Hidden Roles',
    'secret-role': 'Hidden Roles',
    'social deduction': 'Social Deduction',
    'social-deduction': 'Social Deduction',
    'deduce': 'Social Deduction',
    'deducing': 'Social Deduction',
    // Moved deduction to themes section
    'hidden movement': 'Hidden Movement',
    'hidden-movement': 'Hidden Movement',
    'hidden information': 'Hidden Information',
    'hidden-information': 'Hidden Information',
    'secret information': 'Hidden Information',
    'imperfect information': 'Hidden Information',
    'take that': 'Take That',
    'take-that': 'Take That',
    'push your luck': 'Push Your Luck',
    'press your luck': 'Push Your Luck',
    'push-your-luck': 'Push Your Luck',
    'press-your-luck': 'Push Your Luck',
    'memory': 'Memory',
    'memorization': 'Memory',
    'memorization game': 'Memory',
    'pattern recognition': 'Pattern Recognition',
    'pattern-recognition': 'Pattern Recognition',
    'pattern matching': 'Pattern Recognition',
    'pattern-matching': 'Pattern Recognition',
    'recognize patterns': 'Pattern Recognition',
    'match patterns': 'Pattern Recognition',
    'visual patterns': 'Pattern Recognition',

    // Physical mechanics
    'dexterity': 'Dexterity',
    'dexterity game': 'Dexterity',
    'physical skill': 'Dexterity',
    'physical dexterity': 'Dexterity',
    'flicking': 'Flicking',
    'flick': 'Flicking',
    'stacking': 'Stacking',
    'stack': 'Stacking',
    'balancing': 'Dexterity',
    'balance': 'Dexterity',
    'drawing': 'Drawing',
    'draw': 'Drawing',
    'sketch': 'Drawing',
    'sketching': 'Drawing',
    'paper and pencil': 'Paper-and-Pencil',
    'paper-and-pencil': 'Paper-and-Pencil',
    'roll and move': 'Roll / Spin and Move',
    'roll-and-move': 'Roll / Spin and Move',
    'roll & move': 'Roll / Spin and Move',
    'roll to move': 'Roll / Spin and Move',
    'spin and move': 'Roll / Spin and Move',
    'spin-and-move': 'Roll / Spin and Move',
    'spin to move': 'Roll / Spin and Move',

    // Other common mechanics
    'rpg': 'Role Playing',
    'role playing': 'Role Playing',
    'role-playing': 'Role Playing',
    'roleplaying': 'Role Playing',
    'roleplay': 'Role Playing',
    'word': 'Word Game',
    'word game': 'Word Game',
    'words': 'Word Game',
    'vocabulary': 'Word Game',
    'programming': 'Programmed Movement',
    'program': 'Programmed Movement',
    'programmed movement': 'Programmed Movement',
    'programmed-movement': 'Programmed Movement',
    'app driven': 'App Driven',
    'app-driven': 'App Driven',
    'app assisted': 'App Driven',
    'app-assisted': 'App Driven',
    '4x': '4X',
    'explore expand exploit exterminate': '4X',
    'exploration': 'Exploration',
    'explore': 'Exploration',
    'expansion': 'Expansion',
    'expand': 'Expansion',
    'exploitation': 'Resource Management',
    'exploit': 'Resource Management',
    'extermination': 'Area Control',
    'exterminate': 'Area Control',
    'pick up and deliver': 'Pick-up and Deliver',
    'pick-up and deliver': 'Pick-up and Deliver',
    'pickup and deliver': 'Pick-up and Deliver',
    'delivery': 'Pick-up and Deliver',
    'market': 'Market',
    'marketplace': 'Market',
    'markets': 'Market',
    'bag building': 'Bag Building',
    'bag-building': 'Bag Building',
    'build bag': 'Bag Building',
    'building bag': 'Bag Building',
    'voting': 'Voting',
    'vote': 'Voting',
    'votes': 'Voting',
    'betting': 'Betting/Wagering',
    'bet': 'Betting/Wagering',
    'wagering': 'Betting/Wagering',
    'wager': 'Betting/Wagering',
    'trivia': 'Trivia',
    'quiz': 'Trivia',
    'quizzing': 'Trivia',
    'questions': 'Trivia',
    'puzzle': 'Puzzle',
    'puzzles': 'Puzzle',
    'solving puzzles': 'Puzzle',
    'area enclosure': 'Area Enclosure',
    'area-enclosure': 'Area Enclosure',
    'enclose': 'Area Enclosure',
    'enclosing': 'Area Enclosure',
    'tableau': 'Tableau Building',
    'pool building': 'Pool Building',
    'pool-building': 'Pool Building',
    'variable player powers': 'Variable Player Powers',
    'variable-player-powers': 'Variable Player Powers',
    'asymmetric': 'Variable Player Powers',
    'asymmetrical': 'Variable Player Powers',
    'variable powers': 'Variable Player Powers',
    'different powers': 'Variable Player Powers',
    'different abilities': 'Variable Player Powers'
  };

  // Extract themes - always remove matched keywords from search term
  for (const [keyword, theme] of Object.entries(themeKeywords)) {
    const regex = new RegExp(`\\b${keyword}\\b`, 'i');
    if (regex.test(query)) {
      if (!result.themes.includes(theme)) {
        result.themes.push(theme);
      }
      
      // Remove ALL matched theme keywords from the query
      query = query.replace(regex, ' ');
    }
  }

  // Extract mechanics with context awareness
  // Extract combined patterns and specific cases first
  const combinedPatterns = [
    { pattern: /\b(social deduction|social-deduction)\b/i, theme: null, mechanic: 'Deduction' },
    { pattern: /\b(dice rolling|dice-rolling|rolling dice)\b/i, theme: null, mechanic: 'Dice Rolling' },
    { pattern: /\b(card drafting|card-drafting|drafting cards)\b/i, theme: null, mechanic: 'Card Drafting' },
    { pattern: /\b(dice drafting|dice-drafting|drafting dice)\b/i, theme: null, mechanic: 'Dice Drafting' },
    { pattern: /\b(worker placement|worker-placement)\b/i, theme: null, mechanic: 'Worker Placement' },
    { pattern: /\b(area control|area-control)\b/i, theme: null, mechanic: 'Area Control' },
    { pattern: /\b(deck building|deck-building|deckbuilding)\b/i, theme: null, mechanic: 'Deck Building' },
    { pattern: /\b(hidden roles|hidden-roles|secret roles)\b/i, theme: null, mechanic: 'Hidden Roles' },
    { pattern: /\b(hidden movement|hidden-movement)\b/i, theme: null, mechanic: 'Hidden Movement' },
    { pattern: /\b(push your luck|press your luck)\b/i, theme: null, mechanic: 'Push Your Luck' },
    { pattern: /\b(pattern recognition|pattern matching)\b/i, theme: null, mechanic: 'Pattern Recognition' },
    
    // Handle special cases with dual type
    { pattern: /\b(deduction)\b/i, theme: 'Deduction', mechanic: null },
    { pattern: /\b(memory)\b/i, theme: null, mechanic: 'Memory' },
    { pattern: /\b(card game|card games)\b/i, theme: 'Card Game', mechanic: null },
    { pattern: /\b(dice game|dice games)\b/i, theme: 'Dice', mechanic: null },
  ];
  
  // Process combined patterns first to handle special cases
  for (const pattern of combinedPatterns) {
    if (pattern.pattern.test(query)) {
      // Add theme if specified
      if (pattern.theme && !result.themes.includes(pattern.theme)) {
        result.themes.push(pattern.theme);
      }
      
      // Add mechanic if specified
      if (pattern.mechanic && !result.mechanics.includes(pattern.mechanic)) {
        result.mechanics.push(pattern.mechanic);
      }
      
      // Remove the matched pattern
      query = query.replace(pattern.pattern, ' ');
    }
  }
  
  // Now check individual mechanics, but be careful with ambiguous terms
  const ambiguousTerms = ['draft', 'drafting', 'place', 'placement', 'build', 'building', 
                         'area', 'control', 'movement', 'roll', 'spin', 'move', 'hidden', 'secret'];

  // Process mechanics with caution for ambiguous terms
  for (const [keyword, mechanic] of Object.entries(mechanicKeywords)) {
    const regex = new RegExp(`\\b${keyword}\\b`, 'i');
    if (regex.test(query)) {
      // For ambiguous terms, only add if there's no better specific match
      const isAmbiguous = ambiguousTerms.some(term => keyword.toLowerCase().includes(term));
      
      // Still add the mechanic if it's not ambiguous or if no better match was already found
      if (!isAmbiguous || !result.mechanics.some(m => 
          m.toLowerCase().includes(keyword.toLowerCase()) || 
          mechanic.toLowerCase() === m.toLowerCase())) {
        
        if (!result.mechanics.includes(mechanic)) {
          result.mechanics.push(mechanic);
        }
      }
      
      // Remove matched mechanic keywords
      query = query.replace(regex, ' ');
    }
  }

  // Extract common game properties (special handling for popular game types)
  // These can apply multiple filters at once
  const gameTypePatterns = [
    { 
      regex: /\bparty game(s)?\b/i,
      filters: {
        minPlayers: 4,
        weight: 'Light',
        maxTime: '60'
      }
    },
    { 
      regex: /\bfamily game(s)?\b/i,
      filters: {
        weight: 'Light' 
      }
    },
    { 
      regex: /\bstrategy game(s)?\b/i,
      filters: {
        weight: 'Medium'
      }
    },
    { 
      regex: /\bkids game(s)?\b/i,
      filters: {
        themes: ['Kids']
      }
    },
    { 
      regex: /\beurogame(s)?\b/i,
      filters: {
        weight: 'Medium',
        mechanics: ['Resource Management']
      }
    },
    { 
      regex: /\bwargame(s)?\b/i,
      filters: {
        weight: 'Medium Heavy',
        themes: ['War']
      }
    },
    { 
      regex: /\bfillers?\b/i,
      filters: {
        maxTime: '30',
        weight: 'Light'
      }
    }
  ];

  // Check for game type patterns
  for (const pattern of gameTypePatterns) {
    if (pattern.regex.test(query)) {
      // Apply all associated filters
      const filters = pattern.filters;
      
      // For each filter property
      for (const [key, value] of Object.entries(filters)) {
        // For array properties like mechanics, themes
        if (Array.isArray(value)) {
          for (const item of value) {
            if (Array.isArray(result[key]) && !result[key].includes(item)) {
              result[key].push(item);
            }
          }
        } 
        // For scalar properties, only set if not already set
        else if (result[key] === null) {
          result[key] = value;
        }
      }
      
      // Remove the game type from the query
      query = query.replace(pattern.regex, ' ');
    }
  }

  // Clean up and set remaining text as the search term using our helper function
  query = removeFillerWords(query);
  
  // Only set the searchTerm if there's actually text left after all the processing
  if (query && query.trim().length > 0) {
    result.searchTerm = query;
  } else {
    // If all terms were recognized as filters, don't add an empty search term
    delete result.searchTerm;
  }

  // Remove null or empty values for cleaner API requests
  Object.keys(result).forEach(key => {
    if (result[key] === null || 
        result[key] === '' || 
        (Array.isArray(result[key]) && result[key].length === 0)) {
      delete result[key];
    }
  });

  // Add logging to help debug
  console.log('Parsed query:', result);
  console.log('Original query:', originalQuery);

  return result;
}

/**
 * Convert text number words to digits in a string
 * @param {string} text - The input text
 * @return {string} - Text with number words converted to digits
 */
function convertTextNumbersToDigits(text) {
  if (!text) return text;
  
  const numberWords = {
    'one': '1',
    'two': '2',
    'three': '3',
    'four': '4',
    'five': '5',
    'six': '6',
    'seven': '7',
    'eight': '8',
    'nine': '9',
    'ten': '10',
    'eleven': '11',
    'twelve': '12',
    'thirteen': '13',
    'fourteen': '14',
    'fifteen': '15',
    'sixteen': '16',
    'seventeen': '17',
    'eighteen': '18',
    'nineteen': '19',
    'twenty': '20',
    'thirty': '30',
    'forty': '40',
    'fifty': '50',
    'sixty': '60',
    'seventy': '70',
    'eighty': '80',
    'ninety': '90',
    'hundred': '100'
  };

  // Replace number words with digits
  let processedText = text;
  
  // Use word boundaries in regex to avoid partial replacements
  for (const [word, digit] of Object.entries(numberWords)) {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    processedText = processedText.replace(regex, digit);
  }
  
  // Handle compound numbers (twenty-five, etc.)
  for (let i = 21; i <= 99; i++) {
    if (i % 10 === 0) continue; // Skip multiples of 10 as they're handled above
    
    const tensDigit = Math.floor(i / 10) * 10;
    const onesDigit = i % 10;
    
    const tensWord = Object.keys(numberWords).find(key => numberWords[key] === tensDigit.toString());
    const onesWord = Object.keys(numberWords).find(key => numberWords[key] === onesDigit.toString());
    
    if (tensWord && onesWord) {
      // Match both hyphenated and space-separated forms
      const hyphenatedRegex = new RegExp(`\\b${tensWord}-${onesWord}\\b`, 'gi');
      const spacedRegex = new RegExp(`\\b${tensWord}\\s+${onesWord}\\b`, 'gi');
      
      processedText = processedText.replace(hyphenatedRegex, i.toString());
      processedText = processedText.replace(spacedRegex, i.toString());
    }
  }
  
  return processedText;
}

/**
 * Removes common filler words and phrases from a search query
 * @param {string} text - The input text to clean
 * @return {string} - Clean text with filler words removed
 */
function removeFillerWords(text) {
  if (!text) return text;
  
  const fillerWordsPatterns = [
    /\b(game|games|board game|board games|boardgame|boardgames)\b/gi,
    /\b(looking for|find( me)?|search( for)?|about|with|that has|featuring|includes)\b/gi,
    /\b(theme|mechanic|play|playing|recommend|recommendation)\b/gi,
    /\b(good|great|best|fun|awesome|cool|interesting|top|new|popular)\b/gi,
    /\b(for|the|a|an|some|any|me|my|our|we|us|to|in|on|at|of|by|and|or)\b/gi,
    /\bwith\b/gi, // Common connector word
    /\bs\b/gi, // Remove stray 's' that often remains after removing 'games', 'players', etc.
  ];
  
  let cleanedText = text;
  
  fillerWordsPatterns.forEach(pattern => {
    cleanedText = cleanedText.replace(pattern, ' ');
  });
  
  // Clean up extra spaces, dashes, and punctuation that might be left behind
  cleanedText = cleanedText
    .replace(/\s+/g, ' ')
    .replace(/-+/g, ' ')
    .replace(/[.,?!:;'"()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  return cleanedText;
}

/**
 * Extract structured filters from a natural language query
 * @param {string} query - The natural language query
 * @return {Object} - Object with searchTerm and filters properties
 */
export function extractFiltersFromQuery(query) {
  const parsedQuery = parseNaturalLanguageQuery(query);
  
  const { searchTerm, ...filters } = parsedQuery;
  
  return {
    searchTerm,
    filters
  };
}